import React from 'react';
import { VirtualAtonTargetInfo } from '@ais-ui/websockets';
import { useLocalization } from '@ais-ui/locale-management';
import { useDateTimeFormat } from '../conversions/useDateFormat';
import { isNonEmpty } from '@ais-ui/utils';
import { Separator } from '@ais-ui/ui-core';
import { useLengthUnits } from '../Units/useLengthUnits';

interface IdentificationItemProps {
  label: string;
  value: string;
}

const IdentificationItem = ({ label, value }: IdentificationItemProps) => {
  return (
    <div className="flex flex-col gap-1 mt-1">
      <span className="text-sm font-medium flex-none">{label}</span>
      <span className="text-sm text-muted-foreground flex-grow text-ellipsis overflow-hidden whitespace-nowrap select-text">
        {isNonEmpty(value) ? value : 'Unknown'}
      </span>
    </div>
  );
};

interface IdentificationProps {
  virtualAtonInfo: VirtualAtonTargetInfo;
}

const Identification = ({ virtualAtonInfo }: IdentificationProps) => {
  const { messages } = useLocalization();

  return (
    <div className="grid grid-cols-2 gap-4">
      <IdentificationItem
        label={messages.shipDetailsBaseNameLabel}
        value={virtualAtonInfo.name}
      />
      <IdentificationItem
        label={messages.shipDetailsBaseMMSILabel}
        value={virtualAtonInfo.mmsi}
      />
      <IdentificationItem
        label={messages.shipDetailsBaseCountryLabel}
        value={virtualAtonInfo.country}
      />
      <IdentificationItem
        label={messages.reportsDetailsTypeLabel}
        value={virtualAtonInfo.targetType}
      />
      <IdentificationItem
        label={messages.targetDetailsAtonTypeLabel}
        value={virtualAtonInfo.atonType}
      />
    </div>
  );
};

interface PhysicalCharacteristicsProps {
  virtualAtonInfo: VirtualAtonTargetInfo;
}

interface PhysicalCharacteristicsItemProps {
  label: string;
  value: string;
}

const PhysicalCharacteristicsItem = ({
  label,
  value,
}: PhysicalCharacteristicsItemProps) => {
  const { convertLengthString } = useLengthUnits();
  const convertedValue = convertLengthString(value);

  return (
    <div className="flex flex-col gap-1 mt-1">
      <span className="text-sm font-medium flex-none">{label}</span>
      <span className="text-sm text-muted-foreground flex-grow text-ellipsis overflow-hidden whitespace-nowrap select-text">
        {isNonEmpty(convertedValue) ? convertedValue : '-'}
      </span>
    </div>
  );
};

const PhysicalCharacteristics = ({
  virtualAtonInfo,
}: PhysicalCharacteristicsProps) => {
  const { messages } = useLocalization();

  return (
    <div className="grid grid-cols-2 gap-4">
      <PhysicalCharacteristicsItem
        label={messages.shipDetailsBaseWidthLabel}
        value={virtualAtonInfo.width}
      />
      <PhysicalCharacteristicsItem
        label={messages.shipDetailsBaseLengthLabel}
        value={virtualAtonInfo.length}
      />
    </div>
  );
};

interface OperationalStatusProps {
  virtualAtonInfo: VirtualAtonTargetInfo;
}

interface OperationalStatusItemProps {
  label: string;
  value: string;
}

const OperationalStatusItem = ({
  label,
  value,
}: OperationalStatusItemProps) => {
  return (
    <div className="flex flex-col gap-1 mt-1">
      <span className="text-sm font-medium flex-none">{label}</span>
      <span className="text-sm text-muted-foreground flex-grow text-ellipsis overflow-hidden whitespace-nowrap select-text">
        {isNonEmpty(value) ? value : 'Unknown'}
      </span>
    </div>
  );
};

const OperationalStatus = ({ virtualAtonInfo }: OperationalStatusProps) => {
  const { messages } = useLocalization();
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();

  return (
    <div className="grid grid-cols-2 gap-4">
      <OperationalStatusItem
        label={messages.shipDetailsBaseLatLabel}
        value={virtualAtonInfo.latitudeDegrees}
      />
      <OperationalStatusItem
        label={messages.shipDetailsBaseLonLabel}
        value={virtualAtonInfo.longitudeDegrees}
      />
      <OperationalStatusItem
        label={messages.shipDetailsBaseLastAISReportLabel}
        value={formatDateTimeWithTimeZone(virtualAtonInfo.lastAisReport)}
      />
      <OperationalStatusItem
        label={messages.targetDetailsLastAisLatitudeLabel}
        value={virtualAtonInfo.lastAisLatitude}
      />
      <OperationalStatusItem
        label={messages.targetDetailsLastAisLongitudeLabel}
        value={virtualAtonInfo.lastAisLongitude}
      />
      <OperationalStatusItem
        label={messages.targetDetailsLastAisSatLatitudeLabel}
        value={virtualAtonInfo.lastAisSatLatitude}
      />
      <OperationalStatusItem
        label={messages.targetDetailsLastAisSatLongitudeLabel}
        value={virtualAtonInfo.lastAisSatLongitude}
      />
      <OperationalStatusItem
        label={messages.targetDetailsLastAisSatReportLabel}
        value={formatDateTimeWithTimeZone(virtualAtonInfo.lastAisSatReport)}
      />
      <OperationalStatusItem
        label={messages.shipDetailsStateLabel}
        value={virtualAtonInfo.state}
      />
      <OperationalStatusItem
        label={messages.shipDetailsAtonAssignedModeLabel}
        value={virtualAtonInfo.assignedMode}
      />
      <OperationalStatusItem
        label={messages.shipDetailsAtonOffPositionLabel}
        value={virtualAtonInfo.offPosition}
      />
    </div>
  );
};

export const VirtualAtonDetailsPanel = ({
  virtualAtonInfo,
}: {
  virtualAtonInfo: VirtualAtonTargetInfo;
}) => {
  return (
    <div className="grid gap-6">
      <div className="grid gap-4">
        <div>
          <h3 className="text-lg font-medium">
            Identification and Basic Details
          </h3>
          <Separator className="mt-2"></Separator>
        </div>
        <Identification virtualAtonInfo={virtualAtonInfo}></Identification>
      </div>
      <div className="grid gap-4">
        <div>
          <h3 className="text-lg font-medium">Physical Characteristics</h3>
          <Separator className="mt-2"></Separator>
        </div>
        <PhysicalCharacteristics
          virtualAtonInfo={virtualAtonInfo}
        ></PhysicalCharacteristics>
      </div>
      <div className="grid gap-4">
        <div>
          <h3 className="text-lg font-medium">Operational Status</h3>
          <Separator className="mt-2"></Separator>
        </div>
        <OperationalStatus
          virtualAtonInfo={virtualAtonInfo}
        ></OperationalStatus>
      </div>
    </div>
  );
};
